import { compareMinutes } from "helpers/time";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateGeneralForm } from "store/platforms/slice";
import { BroadcastDetailsForm } from "components/forms/BroadcastDetailsForm";
import {
    VideoPlayerForm,
    type GeneralPlatformForm,
    type QualitySelection
} from "store/platforms/types";
import { type RootState } from "store/reducers";
import {
    BroadcastStatus,
    BroadcastWebLink,
    type Category
} from "@switcherstudio/switcher-api-client";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";
import { type SpeedTestResults, SpeedTestStatus } from "hooks/useSpeedTest";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import DEFAULT_THUMBNAIL_URL from "assets/placeholder-multistream.png";

export interface LivestreamDetailsStepProps {
    speedTestStatus: SpeedTestStatus;
    speedTestResults: SpeedTestResults;
    handleRetrySpeedTest: () => void;
    minuteOffset?: number;
    onGeneralFormChange: (update: Partial<GeneralPlatformForm>) => void;
    onVideoPlayerFormChange: (update: Partial<VideoPlayerForm>) => void;
    setStreamQuality: (quality: QualitySelection) => void;
}

export const LivestreamDetailsStep = ({
    minuteOffset,
    speedTestStatus,
    speedTestResults,
    handleRetrySpeedTest,
    setStreamQuality,
    onGeneralFormChange,
    onVideoPlayerFormChange
}: LivestreamDetailsStepProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { userInfo } = useSelector((state: RootState) => state.user);

    const {
        general: { generalForm },
        videoplayer: { videoPlayerForm }
    } = useSelector((state: RootState) => state.platforms);

    const externalDestinations = useMemo<DestinationDescriptor[]>(
        () =>
            generalForm.selectedSimulcastDestinations.filter(
                (d) => d.platform !== PlatformId.VideoPlayer
            ),
        [generalForm.selectedSimulcastDestinations]
    );

    const handleSetTitle = useCallback(
        (title: string) => onGeneralFormChange({ title }),
        [onGeneralFormChange]
    );

    const handleSetDescription = useCallback(
        (description: string) => onGeneralFormChange({ description }),
        [onGeneralFormChange]
    );

    const handleSetStartsAt = useCallback(
        (startsAt: string) => {
            const formUpdate = {
                scheduledStartTime: new Date(startsAt),
                isInvalid: false
            };
            const now = new Date();

            if (
                !!minuteOffset &&
                compareMinutes(formUpdate.scheduledStartTime, now, minuteOffset)
            ) {
                formUpdate.isInvalid = true;
            } else if (formUpdate.scheduledStartTime < now) {
                formUpdate.isInvalid = true;
            }

            dispatch(updateGeneralForm(formUpdate));
        },
        [dispatch, minuteOffset]
    );

    const handleSetShowInCatalog = useCallback(
        (show: boolean) => {
            onGeneralFormChange({ showInCatalog: show });
        },
        [onGeneralFormChange]
    );

    const handleSetThumbnailFile = useCallback(
        (file: File | null) =>
            onGeneralFormChange({
                scheduledImageFile: file,
                hasCustomThumbnail: !!file
            }),
        [onGeneralFormChange]
    );

    const handleSetThumbnailImageURL = useCallback(
        (url: string) => onGeneralFormChange({ scheduledImageFileUrl: url }),
        [onGeneralFormChange]
    );

    const handleOnRemoveThumbnail = useCallback(() => {
        onGeneralFormChange({
            scheduledImageFile: null,
            scheduledImageFileUrl: "",
            hasCustomThumbnail: false
        });
    }, [onGeneralFormChange]);

    const handleSetShowCountdownOverThumbnail = useCallback(
        (show: boolean) => {
            onGeneralFormChange({ showPremiereCountdown: show });
        },
        [onGeneralFormChange]
    );

    const handleSetSelectedCategories = useCallback(
        (categories: Category[]) => {
            onGeneralFormChange({ selectedCategories: categories });
        },
        [onGeneralFormChange]
    );

    const handleSetIsPasswordGatingEnabled = useCallback(
        (enabled: boolean) => {
            onVideoPlayerFormChange({ isPasswordGatingEnabled: enabled });
        },
        [onVideoPlayerFormChange]
    );

    const handleEmailToggleChange = useCallback(
        (enabled: boolean) => {
            onVideoPlayerFormChange({ isEmailGatingEnabled: enabled });
        },
        [onVideoPlayerFormChange]
    );

    const handlePasswordChange = useCallback(
        (password: string) => {
            onVideoPlayerFormChange({ password });
        },
        [onVideoPlayerFormChange]
    );

    const handleSetCreatorProductId = useCallback(
        (creatorProductId: string) => {
            onVideoPlayerFormChange({ creatorProductId });
        },
        [onVideoPlayerFormChange]
    );

    const handleLinkSave = useCallback(
        (links: BroadcastWebLink[]) => {
            onVideoPlayerFormChange({ links });
        },
        [onVideoPlayerFormChange]
    );

    const handleSetAddToPlayers = useCallback(
        (players: string[]): void => {
            onVideoPlayerFormChange({ playlistIds: players });
        },
        [onVideoPlayerFormChange]
    );

    const handleSetEnableLiveShopping = useCallback(
        (enabled: boolean) => {
            onVideoPlayerFormChange({ enableLiveShopping: enabled });
        },
        [onVideoPlayerFormChange]
    );

    const isShopifyCustomer = useMemo(
        () => userInfo?.Claims?.Details?.FeatureClaims?.includes("shopify"),
        [userInfo]
    );

    // Shopify store credentials
    const { data: shopifyStoreCredentials, loading: storeCredentialsLoading } =
        useSwitcherClient((client) => client.shopify_GetCredentials, {
            requestImmediately: isShopifyCustomer,
            args: []
        });

    useEffect(() => {
        if (!generalForm?.scheduledImageFileUrl) {
            onGeneralFormChange({
                scheduledImageFileUrl: DEFAULT_THUMBNAIL_URL
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <h6 className={styles["bold-heading"]}>
                {t("platforms:add-details")}
            </h6>
            <BroadcastDetailsForm
                {...generalForm}
                showThumbnailUploader
                enableRecording
                isScheduledLivestream
                liveBroadcast
                showCategories
                showGatingOptions
                isPasswordGatingEnabled={
                    videoPlayerForm.isPasswordGatingEnabled
                }
                setIsPasswordGatingEnabled={handleSetIsPasswordGatingEnabled}
                password={videoPlayerForm.password}
                selectedCategories={generalForm.selectedCategories}
                handlePasswordChange={handlePasswordChange}
                isEmailGatingEnabled={videoPlayerForm.isEmailGatingEnabled}
                handleEmailToggleChange={handleEmailToggleChange}
                creatorProductId={videoPlayerForm.creatorProductId}
                handleSetCreatorProductId={handleSetCreatorProductId}
                setSelectedCategories={handleSetSelectedCategories}
                thumbnailImageURL={generalForm?.scheduledImageFileUrl}
                showInCatalog={generalForm.showInCatalog}
                showCountdownOverThumbnail={generalForm.showPremiereCountdown}
                streamQuality={generalForm.selectedQuality}
                speedTestResults={speedTestResults}
                speedTestStatus={speedTestStatus}
                handleRetrySpeedTest={handleRetrySpeedTest}
                setShowInCatalog={handleSetShowInCatalog}
                players={videoPlayerForm.playlistIds}
                setAddToPlayers={handleSetAddToPlayers}
                setTitle={handleSetTitle}
                setDescription={handleSetDescription}
                startsAt={generalForm.scheduledStartTime.toISOString()}
                setStartsAt={handleSetStartsAt}
                setStreamQuality={setStreamQuality}
                setThumbnailFile={handleSetThumbnailFile}
                setThumbnailImageURL={handleSetThumbnailImageURL}
                onRemoveThumbnail={handleOnRemoveThumbnail}
                setShowCountdownOverThumbnail={
                    handleSetShowCountdownOverThumbnail
                }
                broadcastStatus={BroadcastStatus._0}
                links={videoPlayerForm.links}
                onLinkSave={handleLinkSave}
                showLiveShoppingToggle={
                    storeCredentialsLoading &&
                    shopifyStoreCredentials?.length > 0
                }
                enableLiveShopping={videoPlayerForm.enableLiveShopping}
                setEnableLiveShopping={handleSetEnableLiveShopping}
                destinations={externalDestinations}
                location="new-livestream"
            />
        </>
    );
};
